import React, { useEffect, useState, useRef } from 'react';
import { AroundMeView } from "./AroundMeView";
import { safeFetch } from "./App";
import { motion } from 'framer-motion';
import { Radar } from "lucide-react";

export const AroundMe = ({ chatId }) => {
    // What's Around Me state
    const [aroundMeRadius, setAroundMeRadius] = useState(5);
    const [aroundMeData, setAroundMeData] = useState(null);
    const [loadingAroundMe, setLoadingAroundMe] = useState(false);
    const [aroundMeLocation, setAroundMeLocation] = useState(null);
    const [requestedAroundMeGeo, setRequestedAroundMeGeo] = useState(false);
    const [locationDenied, setLocationDenied] = useState(false);
    const [error, setError] = useState('');
    const radiusDebounceTimer = useRef(null);

    // Scroll to top whenever entering the AroundMe page (component mount)
    useEffect(() => {
        // Use instant scroll to avoid visible jump artifacts with sticky header
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (radiusDebounceTimer.current) {
                clearTimeout(radiusDebounceTimer.current);
            }
        };
    }, []);

    // Function to fetch "What's Around Me" data
    const fetchWhatAroundMe = async (lat, lon, radius) => {
        setLoadingAroundMe(true);
        setError('');

        try {
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/what-around-me`, chatId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ latitude: lat, longitude: lon, radius_km: radius }),
            });
            const data = await res.json();
            setAroundMeData(data);
        } catch (err) {
            console.error('Failed to fetch what\'s around me:', err);
            setError('שגיאה בקבלת נתונים על מה בסביבתך');
        } finally {
            setLoadingAroundMe(false);
        }
    };

    // Request geolocation when entering "What's Around Me" view
    useEffect(() => {
        if (!requestedAroundMeGeo) {
            setRequestedAroundMeGeo(true);
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        const location = [pos.coords.latitude, pos.coords.longitude];
                        setAroundMeLocation(location);
                        setLocationDenied(false);
                        setError('');
                        // Automatically fetch data when location is obtained
                        fetchWhatAroundMe(pos.coords.latitude, pos.coords.longitude, aroundMeRadius);
                    },
                    (err) => {
                        console.warn('Geolocation error or denied:', err);
                        setLocationDenied(true);
                        setError('לא הצלחנו לקבל את המיקום שלך. אנא אפשר גישה למיקום.');
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
                );
            } else {
                setLocationDenied(true);
                setError('הדפדפן שלך לא תומך בשירותי מיקום');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedAroundMeGeo]);

    const handleRadiusChange = (newRadius) => {
        setAroundMeRadius(newRadius);

        // Clear the previous timer
        if (radiusDebounceTimer.current) {
            clearTimeout(radiusDebounceTimer.current);
        }

        // Set a new timer to fetch data after 500ms of inactivity
        radiusDebounceTimer.current = setTimeout(() => {
            if (aroundMeLocation) {
                fetchWhatAroundMe(aroundMeLocation[0], aroundMeLocation[1], newRadius);
            }
        }, 500);
    };

    return (
        <div className="px-2 pb-6 min-h-screen max-w-6xl mx-auto bg-surface">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
            >


                {/* Selector Panel above Map */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-xl mx-auto flex flex-col gap-3 mb-6"
                >
                    <div className="surface-card p-4">
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-ink font-semibold text-base">רדיוס חיפוש</label>
                            <motion.div
                                key={aroundMeRadius}
                                initial={{ scale: 1.08 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                className="bg-brand-500 text-ink-inverse font-bold text-lg px-4 py-1.5 rounded-pill shadow-sm"
                            >
                                {aroundMeRadius} <span className="text-xs font-normal opacity-90">ק"מ</span>
                            </motion.div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <input
                                    type="range"
                                    min="2"
                                    max="50"
                                    value={aroundMeRadius}
                                    onChange={(e) => handleRadiusChange(Number(e.target.value))}
                                    className="w-full h-2 rounded-pill appearance-none cursor-pointer accent-brand-500 transition-all"
                                    style={{
                                        background: `linear-gradient(to right, #E5E1D9 0%, #E5E1D9 ${100 - ((aroundMeRadius - 2) / 48) * 100}%, #8B9A6B ${100 - ((aroundMeRadius - 2) / 48) * 100}%, #8B9A6B 100%)`
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Loading State */}
                    {loadingAroundMe && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-8 surface-card"
                        >
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            >
                                <Radar size={36} className="text-brand-500 mx-auto mb-2" />
                            </motion.div>
                            <p className="text-ink-muted text-base font-semibold">מחפש מה בסביבתך...</p>
                        </motion.div>
                    )}
                    {/* Error State */}
                    {error && !loadingAroundMe && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-6 bg-danger-50 border border-danger-100 rounded-xl"
                        >
                            <p className="text-danger-700 text-base font-semibold">{error}</p>
                        </motion.div>
                    )}
                </motion.div>

                {/* Results - AroundMeView Component */}
                {!loadingAroundMe && aroundMeData && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                    >
                        <AroundMeView
                            businesses={aroundMeData.nearby_businesses || []}
                            events={aroundMeData.nearby_events || []}
                            barters={aroundMeData.nearby_barters || []}
                            zones={aroundMeData.nearby_zones || []}
                            categories={aroundMeData.categories || []}
                            bartersCategories={aroundMeData.barters_categories || []}
                            chatId={chatId}
                            userLocation={aroundMeLocation}
                            radiusKm={aroundMeRadius}
                        />

                        {/* Cached Indicator */}
                        {aroundMeData.cached && (
                            <p className="text-center text-xs text-ink-subtle mt-4 font-medium">
                                תוצאות מהזיכרון המטמון
                            </p>
                        )}
                    </motion.div>
                )}
            </motion.div>
        </div>
    );
};
